import { toast } from '@/components/snackbar';
import { ReportJobProgress, ReportJobStatus } from '@/query';
import { Button } from '@mui/material';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Acompanhamento de relatórios que rodam em segundo plano no backend.
 *
 * Precisa ficar ACIMA do Router (montado no `main.tsx`): se o acompanhamento morasse na página, navegar para outra tela
 * desmontaria o hook e o usuário perderia o relatório no meio — exatamente o que este mecanismo existe para evitar.
 *
 * Os jobs são acompanhados **por relatório** (`kind`), cada um com seu próprio ciclo de consultas. Um slot único não
 * serviria: disparar o financeiro enquanto o forecast rodava deixava o forecast órfão, congelado no último percentual
 * visto, mesmo tendo terminado no servidor.
 *
 * O aviso é emitido por `toast(...)` do snackbar do tema, com **id fixo por job**: trocar de estado (processando →
 * pronto) é uma atualização do mesmo toast, não um toast novo. Nada é montado por página, então navegar não reanima nem
 * pisca o aviso.
 */

/** Intervalo entre consultas de estado. */
const POLL_INTERVAL_MS = 2000;

export type TrackedJob = {
  jobId: string;
  /** Identifica o relatório. Um job acompanhado por vez, por relatório. */
  kind: string;
  /** Texto base do aviso, ex.: "Relatório financeiro de lotes". */
  title: string;
  /** Para onde o clique no aviso leva quando o relatório fica pronto. */
  resultPath: string;
  status: ReportJobStatus;
  progress: ReportJobProgress;
  error?: string;
};

/**
 * Estado de um job, na forma comum a todos os relatórios.
 *
 * As rotas devolvem o mesmo envelope com `result` de tipo próprio; aqui ele é `unknown` porque o provider não
 * interpreta o resultado — quem lê é a tela, via `getResult<T>()`.
 */
type JobState = {
  jobId: string;
  status: ReportJobStatus;
  progress: ReportJobProgress;
  result: unknown;
  error: string | null;
};

/**
 * Cada relatório passa as próprias funções geradas pelo orval. É o que mantém o provider genérico: ele não conhece
 * nenhuma rota, só o formato do envelope.
 */
type StartParams = {
  kind: string;
  title: string;
  resultPath: string;
  /** Dispara no backend e devolve o `jobId`. */
  start: () => Promise<{ data: { jobId: string; status: ReportJobStatus } }>;
  /** Consulta o estado do job. */
  poll: (jobId: string, signal?: AbortSignal) => Promise<{ data: JobState }>;
  /** Cancela o job. */
  cancel: (jobId: string) => Promise<unknown>;
};

type ReportJobsContextValue = {
  /** Job em acompanhamento para o relatório, se houver. */
  getJob: (kind: string) => TrackedJob | null;
  /** Resultado do último job concluído, por `kind`. */
  getResult: <T>(kind: string) => T | null;
  startJob: (params: StartParams) => Promise<void>;
  /** Pede o cancelamento no backend. O processamento para na fronteira da próxima fatia. */
  cancelJob: (kind: string) => Promise<void>;
  isRunning: (kind: string) => boolean;
  /** Uso interno da ponte de navegação (`useRegisterReportJobsNavigate`). */
  registerNavigate: (fn: (path: string) => void) => void;
};

const ReportJobsContext = createContext<ReportJobsContextValue | null>(null);

export function useReportJobs() {
  const context = useContext(ReportJobsContext);

  if (!context) {
    throw new Error('useReportJobs precisa estar dentro de <ReportJobsProvider>');
  }

  return context;
}

/**
 * Ponte de navegação: o provider vive acima do Router, onde `useNavigate` não existe, mas o clique no aviso precisa
 * navegar. Este hook — chamado de dentro do Router, pelo `CommonLayout` — publica o `navigate` para o provider usar.
 *
 * Não renderiza nada, então remontar a cada tela não tem efeito visual.
 */
export function useRegisterReportJobsNavigate() {
  const navigate = useNavigate();
  const context = useContext(ReportJobsContext);
  const register = context?.registerNavigate;

  useEffect(() => {
    register?.((path: string) => navigate(path));
  }, [navigate, register]);
}

const toastIdOf = (jobId: string) => `report-job-${jobId}`;

export function ReportJobsProvider({ children }: { children: React.ReactNode }) {
  const [jobs, setJobs] = useState<Record<string, TrackedJob>>({});

  // Resultados ficam fora do state: a tela lê sob demanda e não precisa re-renderizar o app inteiro ao chegarem.
  const resultsRef = useRef<Map<string, unknown>>(new Map());
  const navigateRef = useRef<((path: string) => void) | null>(null);

  // Tudo abaixo é por `kind`. Ficam em ref, e não no state, para que o ciclo de consultas de um relatório não seja
  // reiniciado quando outro relatório muda de estado.
  const handlersRef = useRef(new Map<string, Pick<StartParams, 'poll' | 'cancel'>>());
  const timersRef = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const abortsRef = useRef(new Map<string, AbortController>());
  /** Job atualmente acompanhado de cada relatório. Deixa a resposta atrasada de um job antigo ser descartada. */
  const trackedIdsRef = useRef(new Map<string, string>());

  const registerNavigate = useCallback((fn: (path: string) => void) => {
    navigateRef.current = fn;
  }, []);

  const stopPolling = useCallback((kind: string) => {
    const timer = timersRef.current.get(kind);

    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(kind);
    }

    abortsRef.current.get(kind)?.abort();
    abortsRef.current.delete(kind);
  }, []);

  // setTimeout em cadeia, não setInterval: resposta lenta não empilha consultas em cima da anterior.
  const startPolling = useCallback((kind: string, jobId: string) => {
    const tick = async () => {
      const handlers = handlersRef.current.get(kind);
      if (!handlers || trackedIdsRef.current.get(kind) !== jobId) return;

      const controller = new AbortController();
      abortsRef.current.set(kind, controller);

      try {
        const { data: state } = await handlers.poll(jobId, controller.signal);

        // Outro job deste relatório começou enquanto esta resposta vinha: descarta.
        if (trackedIdsRef.current.get(kind) !== jobId) return;

        if (state.status === 'done' && state.result) {
          resultsRef.current.set(kind, state.result);
        }

        setJobs((current) => {
          const tracked = current[kind];
          if (!tracked || tracked.jobId !== state.jobId) return current;

          return {
            ...current,
            [kind]: {
              ...tracked,
              status: state.status,
              progress: state.progress,
              error: state.error ?? undefined
            }
          };
        });

        if (state.status === 'running') {
          timersRef.current.set(kind, setTimeout(tick, POLL_INTERVAL_MS));
        }
      } catch (error: any) {
        // Abortado no unmount ou por um job novo: a rejeição chega depois, e aqui não é erro.
        if (trackedIdsRef.current.get(kind) !== jobId) return;

        // Job expirado (TTL de 1 h) ou de outro processo: estado normal, não falha técnica.
        //
        // O formato do erro muda com o transporte: em web o mutator lança o CORPO da resposta do Fastify
        // (`{ statusCode, error, message }`); no app, pelo `fdm://`, lança um `Error` só com a mensagem. Por isso a
        // mensagem do backend é o fallback — ela já é escrita para o usuário.
        const message =
          error?.statusCode === 404 || error?.status === 404
            ? 'O relatório expirou. Gere novamente.'
            : error?.message ?? 'Falha ao consultar o relatório.';

        setJobs((current) => {
          const tracked = current[kind];
          if (!tracked || tracked.jobId !== jobId) return current;

          return { ...current, [kind]: { ...tracked, status: 'error', error: message } };
        });
      }
    };

    timersRef.current.set(kind, setTimeout(tick, POLL_INTERVAL_MS));
  }, []);

  // Derruba tudo que estiver em voo quando o app fecha.
  useEffect(() => {
    const timers = timersRef.current;
    const aborts = abortsRef.current;

    return () => {
      for (const timer of timers.values()) clearTimeout(timer);
      for (const controller of aborts.values()) controller.abort();

      timers.clear();
      aborts.clear();
    };
  }, []);

  /**
   * Reflete o estado de cada job no seu aviso.
   *
   * O `id` é o do job, então cada transição ATUALIZA o mesmo toast em vez de criar outro — é isso que mantém o aviso
   * estável enquanto o usuário navega. A assinatura evita reemitir o aviso de um job parado só porque outro relatório
   * mudou de estado, o que reiniciaria a contagem de fechamento do toast.
   */
  const emittedRef = useRef(new Map<string, string>());

  useEffect(() => {
    for (const job of Object.values(jobs)) {
      const signature = `${job.status}-${job.progress.percentage}-${job.error ?? ''}`;
      if (emittedRef.current.get(job.jobId) === signature) continue;

      emittedRef.current.set(job.jobId, signature);

      const toastId = toastIdOf(job.jobId);

      if (job.status === 'running') {
        // O estado "running" tem dois momentos: antes da primeira medição do backend não há total, e o percentual só
        // aparece depois. Manter o título curto o bastante para caber numa linha nos dois casos evita que o toast mude
        // de altura no meio do processamento — o que reposicionaria o aviso sob o cursor e desalinharia o destaque do
        // onboarding. Pelo mesmo motivo a descrição é fixa: nada nela depende do progresso.
        const pct = job.progress.total > 0 ? ` — ${job.progress.percentage}%` : '';

        toast.loading(`${job.title}${pct}`, {
          id: toastId,
          description: 'Gerando em segundo plano. Avisaremos aqui quando ficar pronto.',
          duration: Infinity,
          // Fechar aqui daria a impressão de ter cancelado o processamento, que segue no servidor.
          closeButton: false
        });

        continue;
      }

      if (job.status === 'done') {
        toast.success(`${job.title} está pronto`, {
          id: toastId,
          // Precisa ser explícita: o sonner atualiza um toast existente com `{...anterior, ...novo}`, então omitir a
          // descrição aqui deixaria a do estado "carregando" ("avisaremos quando estiver pronto") viva no de sucesso.
          description: 'O resultado já está disponível na tela do relatório.',
          duration: Infinity,
          closeButton: true,
          action: (
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              // O conteúdo do toast é `flex: 1 1 auto` e o botão de fechar é absoluto no canto: sem o `flexShrink` o
              // botão é esmagado por títulos longos, e sem a margem ele fica por baixo do "×".
              sx={{ flexShrink: 0, mr: 2.5 }}
              onClick={() => {
                navigateRef.current?.(job.resultPath);
                toast.dismiss(toastId);
              }}
            >
              Ver relatório
            </Button>
          )
        });

        continue;
      }

      if (job.status === 'canceled') {
        toast.warning(`${job.title} cancelado`, { id: toastId, closeButton: true });
        continue;
      }

      toast.error(`Falha ao carregar ${job.title.toLowerCase()}`, {
        id: toastId,
        description: job.error,
        duration: Infinity,
        closeButton: true
      });
    }
  }, [jobs]);

  const startJob = useCallback(
    async (params: StartParams) => {
      const { kind } = params;

      stopPolling(kind);

      // O aviso do job anterior deste relatório não vale mais. Sem isso ele fica na tela para sempre, congelado no
      // último percentual, ao lado do aviso do job novo.
      const previousJobId = trackedIdsRef.current.get(kind);
      if (previousJobId) {
        toast.dismiss(toastIdOf(previousJobId));
        emittedRef.current.delete(previousJobId);
      }

      // Resultado anterior do mesmo relatório não vale mais.
      resultsRef.current.delete(kind);

      // Antes do `start`: se o disparo falhar, o job anterior já não é mais acompanhado de qualquer forma.
      handlersRef.current.set(kind, { poll: params.poll, cancel: params.cancel });
      trackedIdsRef.current.delete(kind);

      const { data: response } = await params.start();

      trackedIdsRef.current.set(kind, response.jobId);

      setJobs((current) => ({
        ...current,
        [kind]: {
          jobId: response.jobId,
          kind,
          title: params.title,
          resultPath: params.resultPath,
          status: response.status,
          progress: { processed: 0, total: 0, percentage: 0 }
        }
      }));

      startPolling(kind, response.jobId);
    },
    [startPolling, stopPolling]
  );

  const cancelJob = useCallback(async (kind: string) => {
    const jobId = trackedIdsRef.current.get(kind);
    const handlers = handlersRef.current.get(kind);

    if (!jobId || !handlers) return;

    await handlers.cancel(jobId);

    // Não espera o próximo poll: o usuário acabou de pedir, o retorno tem que ser imediato.
    setJobs((current) => {
      const tracked = current[kind];
      if (!tracked || tracked.jobId !== jobId) return current;

      return { ...current, [kind]: { ...tracked, status: 'canceled' } };
    });
  }, []);

  const getResult = useCallback(<T,>(kind: string) => (resultsRef.current.get(kind) as T) ?? null, []);

  const getJob = useCallback((kind: string) => jobs[kind] ?? null, [jobs]);

  const isRunning = useCallback((kind: string) => jobs[kind]?.status === 'running', [jobs]);

  const value: ReportJobsContextValue = { getJob, getResult, startJob, cancelJob, isRunning, registerNavigate };

  return <ReportJobsContext.Provider value={value}>{children}</ReportJobsContext.Provider>;
}
